import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, Loader2, Sparkles } from 'lucide-react';
import { GradientButton } from './ui/gradient-button';

const InputField = ({ label, name, placeholder, value, onChange, required = false, type = "text", delay = 0 }) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay }}
    className="space-y-2 flex-1 group"
  >
    <label className="text-[10px] font-bold text-nebula-gold/70 uppercase tracking-widest ml-1 transition-colors group-focus-within:text-nebula-gold">
      {label} {required && <span className="text-red-400/80">*</span>}
    </label>
    <div className="relative">
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3.5 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-nebula-gold/50 focus:bg-white/[0.07] transition-all duration-300 hover:border-white/20 hover:bg-white/[0.05]"
      />
      <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-nebula-gold/20 to-transparent opacity-0 group-focus-within:opacity-100 pointer-events-none transition-opacity duration-500" style={{ maskImage: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)', maskComposite: 'exclude', padding: '1px' }} />
    </div>
  </motion.div>
);

const SelectField = ({ label, name, value, onChange, options, delay = 0 }) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay }}
    className="space-y-2 flex-1 group"
  >
    <label className="text-[10px] font-bold text-nebula-gold/70 uppercase tracking-widest ml-1 transition-colors group-focus-within:text-nebula-gold">
      Interés Principal
    </label>
    <div className="relative">
      <select 
        name={name} 
        value={value} 
        onChange={onChange} 
        className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3.5 text-sm text-white focus:outline-none focus:border-nebula-gold/50 focus:bg-white/[0.07] transition-all duration-300 appearance-none hover:border-white/20 cursor-pointer"
      >
        <option value="" className="bg-[#0a0a0a] text-white/50">Selecciona una opción...</option>
        {options.map(opt => (
          <option key={opt.value} value={opt.value} className="bg-[#0a0a0a] text-white">{opt.label}</option>
        ))}
      </select>
      <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-white/30">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </div>
    </div>
  </motion.div>
);

const AnalysisModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    business: '',
    service: '',
    budget: '',
    userName: '',
    phone: '',
    email: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (isOpen) {
        setSubmitted(false);
        setLoading(false);
        setFormData({
            business: '',
            service: '',
            budget: '',
            userName: '',
            phone: '',
            email: '',
            message: '',
        });
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setLoading(false);
    setSubmitted(true);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md"
          />
          
          {/* Modal Container */}
          <div className="fixed inset-0 z-[101] flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", duration: 0.5, bounce: 0.3 }}
              className="w-full max-w-2xl bg-[#080808] border border-white/10 rounded-[32px] shadow-2xl relative pointer-events-auto flex flex-col max-h-[90vh]"
            >
              {/* Background Decorations */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-nebula-gold/5 rounded-full blur-[80px] pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-nebula-gold/5 rounded-full blur-[60px] pointer-events-none" />

              {/* Close Button - Sticky/Fixed relative to modal */}
              <button 
                onClick={onClose} 
                className="absolute top-4 right-4 md:top-6 md:right-6 p-2 bg-black/20 hover:bg-white/10 text-white/50 hover:text-white rounded-full transition-all z-50 backdrop-blur-sm border border-white/5"
              >
                <X size={20} />
              </button>

              {/* Scrollable Content Area */}
              <div className="relative z-10 overflow-y-auto overflow-x-hidden flex-1 p-6 md:p-10 custom-scrollbar">
                {!submitted ? (
                  <div>
                    <motion.div 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-8 pr-8"
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-nebula-gold/10 rounded-lg border border-nebula-gold/20">
                            <Sparkles className="w-5 h-5 text-nebula-gold" />
                        </div>
                        <h3 className="text-2xl md:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-white/60">
                            Inicia tu Transformación
                        </h3>
                      </div>
                      <p className="text-white/40 text-sm ml-1">
                        Completa el formulario y nuestros expertos en IA analizarán tu caso gratuitamente.
                      </p>
                    </motion.div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <InputField 
                            label="Tu Nombre" 
                            name="userName" 
                            placeholder="Ej: Alejandro Magno" 
                            value={formData.userName} 
                            onChange={handleInputChange} 
                            required 
                            delay={0.1}
                        />
                        <InputField 
                            label="Nombre del Negocio" 
                            name="business" 
                            placeholder="Ej: Imperio S.L." 
                            value={formData.business} 
                            onChange={handleInputChange} 
                            required 
                            delay={0.15}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <SelectField
                            name="service"
                            value={formData.service}
                            onChange={handleInputChange}
                            options={[
                                { value: "web", label: "Desarrollo Web Premium" },
                                { value: "ai_chat", label: "Chatbots & Atención IA" },
                                { value: "ai_voice", label: "Agentes de Voz IA" },
                                { value: "automation", label: "Automatización de Procesos" },
                                { value: "marketing", label: "Growth Marketing & Ads" },
                                { value: "consulting", label: "Consultoría Estratégica" }
                            ]}
                            delay={0.2}
                        />
                        <InputField 
                            label="Email Corporativo" 
                            name="email" 
                            type="email"
                            placeholder="hola@tuempresa.com" 
                            value={formData.email} 
                            onChange={handleInputChange} 
                            required 
                            delay={0.25}
                        />
                      </div>

                      <InputField 
                        label="Teléfono de Contacto" 
                        name="phone" 
                        type="tel"
                        placeholder="+34 600 000 000" 
                        value={formData.phone} 
                        onChange={handleInputChange} 
                        required 
                        delay={0.3}
                      />

                      <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.35 }}
                        className="space-y-2"
                      >
                        <label className="text-[10px] font-bold text-nebula-gold/70 uppercase tracking-widest ml-1">
                            Detalles del Proyecto
                        </label>
                        <textarea 
                            name="message" 
                            value={formData.message} 
                            onChange={handleInputChange} 
                            placeholder="Cuéntanos tus objetivos, desafíos actuales o qué te gustaría lograr..." 
                            className="w-full h-32 bg-white/[0.03] border border-white/10 rounded-xl p-4 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-nebula-gold/50 focus:bg-white/[0.07] transition-all duration-300 resize-none hover:border-white/20"
                        />
                      </motion.div>

                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.4 }}
                        className="pt-2 pb-2"
                      >
                        <GradientButton 
                            type="submit" 
                            disabled={loading} 
                            className="w-full h-14 text-base tracking-wide"
                        >
                            {loading ? (
                                <span className="flex items-center gap-2">
                                    <Loader2 className="animate-spin" size={18} />
                                    PROCESANDO...
                                </span>
                            ) : (
                                "SOLICITAR ANÁLISIS GRATUITO"
                            )}
                        </GradientButton>
                        <p className="text-center text-white/20 text-[10px] mt-4 uppercase tracking-wider">
                            Sin compromiso • Respuesta en 24h
                        </p>
                      </motion.div>
                    </form>
                  </div>
                ) : (
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="h-full flex flex-col items-center justify-center min-h-[400px]"
                  >
                    <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mb-6 border border-green-500/20 shadow-[0_0_40px_rgba(34,197,94,0.2)]">
                        <CheckCircle className="w-10 h-10 text-green-500" />
                    </div>
                    <h3 className="text-3xl font-bold text-white mb-4">¡Solicitud Recibida!</h3>
                    <p className="text-white/50 text-lg max-w-md mx-auto leading-relaxed mb-8 text-center">
                        Hemos recibido tus datos correctamente. Nuestro equipo de estrategia analizará tu caso y te contactará en breve.
                    </p>
                    <GradientButton onClick={onClose} className="min-w-[200px]">
                        VOLVER AL SITIO
                    </GradientButton>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AnalysisModal;
